import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@lib/toasts'
import { useDeleteApprovalDomainApprover } from '@hooks/useApprovalDomainApprovers'
import { useModal } from '@hooks/useModal'
import * as Sentry from '@sentry/react'

interface ApprovalDomain {
  id: number
  name: string
  globalId: string
}

interface Approver {
  id: number
  user: { name: string }
}

export const useApproverManagement = (
  approvalDomain: ApprovalDomain | undefined,
  refetch: () => void,
) => {
  const { t } = useTranslation()
  const [editingApprover, setEditingApprover] = useState<number>()
  const [showAddApproverModal, setShowAddApproverModal] = useState(false)
  const modal = useModal()
  const deleteApprovalDomainApprover = useDeleteApprovalDomainApprover()

  const handleDeleteApprover = (approver: Approver) => {
    if (!approvalDomain) return

    const deleteApprover = async (approver: Approver) => {
      try {
        await deleteApprovalDomainApprover.mutateAsync({
          approvalDomainGlobalId: approvalDomain.globalId,
          approverId: approver.id,
        })

        toast({
          title: t('features.approvalDomainApprovers.approverDeleted'),
          variant: 'success',
          content: t('features.approvalDomainApprovers.successfullyRemovedApprover', {
            approverName: approver.user.name,
            domainName: approvalDomain.name,
          }),
        })
        refetch()
      } catch (e) {
        Sentry.captureException(e, {
          tags: {
            approvalDomainId: approvalDomain.id,
            approverId: approver.id,
          },
        })
        toast({
          title: t('common.error'),
          variant: 'error',
          content: t('features.approvalDomainApprovers.unableToRemoveApprover'),
        })
      }
    }

    modal.confirm({
      title: t('features.approvalDomainApprovers.deleteApprover'),
      content: t('features.approvalDomainApprovers.confirmDeleteApprover', {
        approverName: approver.user.name,
        domainName: approvalDomain.name,
      }),
      confirmText: t('common.delete'),
      onConfirmAsync: () => deleteApprover(approver),
    })
  }

  const handleApproverSuccess = () => {
    toast({
      title: t('features.approvalDomainApprovers.approversUpdated'),
      variant: 'success',
      content: t('features.approvalDomainApprovers.successfullyUpdatedApprovers', {
        name: approvalDomain?.name,
      }),
    })
    refetch()
  }

  const handleApproverError = () => {
    toast({
      title: t('common.error'),
      variant: 'error',
      content: t('features.approvalDomainApprovers.failedToUpdateApprovers', {
        name: approvalDomain?.name,
      }),
    })
  }

  return {
    editingApprover,
    setEditingApprover,
    showAddApproverModal,
    setShowAddApproverModal,
    handleDeleteApprover,
    handleApproverSuccess,
    handleApproverError,
  }
}